import React, { useEffect, useRef, useState } from 'react';

import cx from 'classnames';

import EditableText from 'components/shared/EditableText';
import Tooltip from 'components/shared/Tooltip';
import useForceUpdate from 'hooks/useForceUpdate';
import { LANDING_THEMES } from 'utils/itineraries/functions';

import s from './index.module.css';
import { ICoverHeadlineProps } from './types';
import Wrapper from './Wrapper';

const DEFAULT_THEME_INITIAL_FONT_SIZE = 60;
const NOMAD_THEME_INITIAL_FONT_SIZE = 72;
const JBD_THEME_INITIAL_FONT_SIZE = 72;

const getAdditionalLineHeightByTheme = (theme: string) => {
  switch (theme) {
    case LANDING_THEMES.nomad:
      return 6;
    case LANDING_THEMES.jbd:
      return 17;
    default:
      return 6;
  }
};

function CoverHeadline({ title, readOnly, headlineHint, canEdit = false, onEdit, landingTheme }: ICoverHeadlineProps) {
  const headlineEl = useRef<HTMLDivElement>(null);
  const containerEl = useRef<HTMLDivElement>(null);
  const headlineRect = headlineEl?.current?.getBoundingClientRect();
  const hintText = headlineHint || 'This text can only be updated in the details tab.';
  const forceUpdate = useForceUpdate();
  const initialFontSize = () => {
    if (landingTheme === LANDING_THEMES.nomad) {
      return NOMAD_THEME_INITIAL_FONT_SIZE;
    }

    if (landingTheme === LANDING_THEMES.jbd) {
      return JBD_THEME_INITIAL_FONT_SIZE;
    }

    return DEFAULT_THEME_INITIAL_FONT_SIZE;
  };
  const [headlineFontSize, setHeadlineFontSize] = useState(initialFontSize);

  useEffect(() => {
    function handleResize() {
      const relFontsize = headlineEl && headlineEl.current ? headlineEl.current.offsetWidth * 0.12 : 0;

      if (relFontsize < headlineFontSize) return setHeadlineFontSize(relFontsize);
      return setHeadlineFontSize(headlineFontSize);
    }

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [headlineFontSize]);

  useEffect(() => {
    forceUpdate();
  }, []);

  if (canEdit) {
    const handleChange = (enteredValue: string) => {
      const obj = { headline: enteredValue };
      onEdit(obj);
    };

    return (
      <div className={cx(s.headlineContainer, s.headlineContainerEdit, 'custom-headline-container')} ref={containerEl}>
        <Wrapper
          innerRef={headlineEl}
          headlineFontSize={headlineFontSize}
          additionalLineHeight={getAdditionalLineHeightByTheme(landingTheme)}
        >
          <EditableText
            text={title}
            textAlignment={landingTheme === LANDING_THEMES.jbd ? 'center' : undefined}
            onChange={handleChange}
            placeholder="Headline"
            isReadonly={readOnly}
            isLineBreak
          />
        </Wrapper>
      </div>
    );
  }

  return (
    <div
      className={cx(s.headlineContainer, 'custom-headline-container')}
      ref={containerEl}
      data-tip
      data-for={'headline_container'}
    >
      <Wrapper
        innerRef={headlineEl}
        headlineFontSize={headlineFontSize}
        additionalLineHeight={getAdditionalLineHeightByTheme(landingTheme)}
      >
        {title}
      </Wrapper>

      <Tooltip place="top" id="headline_container" disable={readOnly || !headlineRect}>
        {hintText}
      </Tooltip>
    </div>
  );
}

export default CoverHeadline;
